import { useNavigate } from 'react-router-dom';
import { postMessageToParent } from '../message-bus/postMessageToParent';

// Capture the initial pathname at module load time (before React Router changes it)
let initialPathname = typeof window !== 'undefined' ? window.location.pathname : '/extension';

export function setInitialPathnameForTest(pathname: string): void {
	initialPathname = pathname;
}

/**
 * Returns true if the iframe was loaded from the New Tab Page (NTP) context.
 * This is determined by the initial pathname when the iframe first loaded,
 * not the current route (which can change via React Router navigation).
 */
export function isNtpContext(): boolean {
	return initialPathname === '/extension';
}

interface UseExtensionNavigationResult {
	navigateToHome: () => void;
	navigateToRovoNtp: () => void;
	navigateToOptionsPage: () => void;
	navigateToChat: () => void;
	navigateToChatWithPrompt: (prompt: string) => void;
}

export function useExtensionNavigation(): UseExtensionNavigationResult {
	const navigate = useNavigate();

	const navigateToHome = (): void => {
		if (isNtpContext()) {
			navigate('/extension');
		} else {
			// In non-NTP contexts (like options), ask the parent page to open the NTP via full page refresh.
			// This is necessary to ensure we properly initialize window message event listeners via ntp.js
			postMessageToParent({ type: 'NAVIGATE_TO_HOME' });
		}
	};

	const navigateToRovoNtp = (): void => {
		if (isNtpContext()) {
			navigate('/extension');
		} else {
			// In non-NTP contexts (like options), ask the parent page to open the Rovo Extension NTP directly,
			// bypassing any user/admin-configured NTP redirect URL.
			postMessageToParent({ type: 'NAVIGATE_TO_ROVO_NTP' });
		}
	};

	const navigateToOptionsPage = (): void => {
		navigate('/extension/options');
	};

	const navigateToChat = (): void => {
		navigate('/extension/rovo-chat?fullPageChat=true');
	};

	const navigateToChatWithPrompt = (prompt: string): void => {
		const encodedPrompt = encodeURIComponent(prompt);
		navigate(
			`/extension/rovo-chat?rovoChatPathway=chat&rovoChatPrompt=${encodedPrompt}&fullPageChat=true`,
		);
	};

	return {
		navigateToHome,
		navigateToRovoNtp,
		navigateToOptionsPage,
		navigateToChat,
		navigateToChatWithPrompt,
	};
}
